import { useEffect, useState } from 'react'
import {
  CATALOG_LINE,
  comingSoonBadgeStyle,
  getDefaultVariant,
  isVariantAddToCartDisabled,
  lowStockBadgeStyle,
  peptideDetailPath,
  resolveVariants,
  stockAlertLabel,
} from '../config'
import { useProducts } from '../context/ProductsContext'
import { useCart } from '../context/CartContext'
import Button from './Button'
import Badge from './Badge'
import ProductVisual from './ProductVisual'
import ProductVariantSizeChips from './ProductVariantSizeChips'
import './ProductCard.css'

export default function ProductCard({ product }) {
  const { saleBadgeLabel, showCompareAtPrice, inventory } = useProducts()
  const { addItem, openCart } = useCart()
  const detailTo = peptideDetailPath(product.id)
  const cardCopy = product.summary ?? product.description
  const variants = resolveVariants(product)
  const defaultV = getDefaultVariant(product)
  const [variantId, setVariantId] = useState(defaultV.id)

  useEffect(() => {
    setVariantId(getDefaultVariant(product).id)
  }, [product.id])

  const selected  = variants.find((v) => v.id === variantId) ?? defaultV
  const saleLabel = saleBadgeLabel()
  const showCompare = showCompareAtPrice(selected)
  const detailCtaLabel = product.catalogLine === CATALOG_LINE.BEAUTY ? 'View product' : 'View peptide'

  // Preorder overrides all stock-based states
  const isPreorder       = variants.some(v => inventory?.[`${product.id}__${v.id}`]?.preorder)
  const stockLabel       = isPreorder ? null  : stockAlertLabel(product, selected, inventory)
  const addToCartDisabled = isPreorder ? false : isVariantAddToCartDisabled(product, selected, inventory)

  return (
    <article className={`product-card${isPreorder ? ' product-card--preorder' : ''}`}>
      <div className={`product-card__visual${product.imageKey ? ' product-card__visual--photo' : ''}`}>
        <div className={`product-card__visual-inner${product.imageKey ? ' product-card__visual-inner--photo' : ''}`}>
          <ProductVisual
            product={product}
            concentration={selected.concentration}
            labelSize={selected.labelSize}
          />
        </div>
      </div>

      <div className="product-card__badges">
        {product.badge ? <Badge>{product.badge}</Badge> : null}
        {saleLabel ? (
          <Badge style={{ color: '#fec8a8', borderColor: 'rgba(255, 160, 120, 0.45)', background: 'rgba(80, 36, 24, 0.35)' }}>
            {saleLabel}
          </Badge>
        ) : null}
        {isPreorder ? (
          <Badge style={{ color: '#fb923c', borderColor: 'rgba(251,146,60,0.45)', background: 'rgba(80,40,10,0.45)' }}>
            Pre-order
          </Badge>
        ) : stockLabel === 'Low stock' ? (
          <Badge style={lowStockBadgeStyle}>Low stock</Badge>
        ) : stockLabel === 'Coming soon' ? (
          <Badge style={comingSoonBadgeStyle}>Coming soon</Badge>
        ) : stockLabel === 'Out of stock' ? (
          <Badge
            style={{
              color: '#f0c0c0',
              borderColor: 'rgba(255, 140, 140, 0.5)',
              background: 'rgba(60, 22, 22, 0.55)',
            }}
          >
            {stockLabel}
          </Badge>
        ) : null}
      </div>

      <h3 className="product-card__title">{product.name}</h3>
      <p className="product-card__subtitle">{product.subtitle}</p>
      <p className="product-card__summary">{cardCopy}</p>

      <ul className="product-card__benefits">
        {product.benefits.map((b) => (
          <li key={b} className="product-card__benefit">
            {b}
          </li>
        ))}
      </ul>

      <ProductVariantSizeChips
        product={product}
        selectedId={variantId}
        onSelectId={setVariantId}
        inventory={inventory}
        productId={product.id}
      />

      <div className="product-card__price">
        {showCompare ? <span className="product-card__price-compare">${selected.compareAtPrice}</span> : null}
        <span className="product-card__price-current">${selected.price}</span>
      </div>

      <div className="product-card__actions">
        <Button variant="primary" to={detailTo} style={{ width: '100%' }}>
          {detailCtaLabel}
        </Button>
        <Button
          variant="outline"
          disabled={addToCartDisabled}
          onClick={addToCartDisabled ? undefined : () => { addItem(product, selected, 1); openCart() }}
          style={{ width: '100%' }}
        >
          {isPreorder ? 'Pre-order' : 'Add to cart'}
        </Button>
      </div>
    </article>
  )
}
